import ReactECharts from 'echarts-for-react';

export function PaceOfPlayChart2() {

  // Holes 1-18 for the angle axis (replacing hours)
  const holes = Array.from({ length: 18 }, (_, i) => `Hole ${i + 1}`);

  // Segments for the radius axis (replacing days)
  const segments = ['Tee', 'Fairway', 'Green'];

  // Sample data: groups on the course
  // Format: { groupNumber: number, hole: number, segment: 'tee' | 'fairway' | 'green' }
  const groupsData = [
    { groupNumber: 1, hole: 1, segment: 'tee' },
    { groupNumber: 2, hole: 1, segment: 'tee' },
    { groupNumber: 3, hole: 1, segment: 'fairway' },
    { groupNumber: 4, hole: 1, segment: 'tee' },
    { groupNumber: 5, hole: 1, segment: 'tee' },
    { groupNumber: 6, hole: 1, segment: 'fairway' },
    { groupNumber: 7, hole: 1, segment: 'green' },
    { groupNumber: 8, hole: 5, segment: 'tee' },
    { groupNumber: 9, hole: 7, segment: 'fairway' },
    { groupNumber: 10, hole: 9, segment: 'green' },
    { groupNumber: 11, hole: 12, segment: 'tee' },
    { groupNumber: 12, hole: 15, segment: 'fairway' },
    { groupNumber: 13, hole: 18, segment: 'green' },
  ];

  // Map segment names to indices
  const segmentMap: { [key: string]: number } = {
    'tee': 0,
    'fairway': 1,
    'green': 2
  };

  // Aggregate groups by location (hole + segment) and count them
  const locationCounts: { [key: string]: { count: number; groups: typeof groupsData } } = {};
  groupsData.forEach(group => {
    const key = `${group.hole}-${group.segment}`;
    if (!locationCounts[key]) {
      locationCounts[key] = { count: 0, groups: [] };
    }
    locationCounts[key].count++;
    locationCounts[key].groups.push(group);
  });

  // Color mapping based on group count
  const getColorForGroupCount = (count: number): string => {
    if (count === 1) return '#4CAF50';      // Green for 1 group
    if (count === 2) return '#FFC107';      // Amber/Yellow for 2 groups
    if (count === 3) return '#FF9800';     // Orange for 3 groups
    if (count === 4) return '#F44336';      // Red for 4 groups
    return '#9C27B0';                       // Purple for 5+ groups
  };

  // Create data points: one per location with group count
  const chartData: any[] = [];
  Object.keys(locationCounts).forEach(key => {
    const [holeStr, segment] = key.split('-');
    const hole = parseInt(holeStr);
    const segmentIndex = segmentMap[segment];
    const holeIndex = hole - 1; // Convert hole number (1-18) to index (0-17)
    const groupCount = locationCounts[key].count;
    
    chartData.push({
      value: [segmentIndex, holeIndex, groupCount], // Store group count in the value
      segmentIndex,
      holeIndex,
      segment,
      groupCount,
      groups: locationCounts[key].groups, // Store groups for tooltip
      itemStyle: {
        color: getColorForGroupCount(groupCount)
      }
    });
  });

  // Generate graphic elements for fairway background
  // Create curved pie slices (sectors) for each hole to form a continuous ring
  const generateFairwayBackground = () => {
    const sectors = [];
    const anglePerHole = 360 / 18; // 20 degrees per hole
    
    for (let i = 0; i < 18; i++) {
      const startAngle = (i * anglePerHole - 90) * Math.PI / 180; // Convert to radians, offset by -90
      const endAngle = ((i + 1) * anglePerHole - 90) * Math.PI / 180;
      
      sectors.push({
        type: 'sector',
        left: '50%',
        top: '50%',
        shape: {
          r0: '33%', // Inner radius - start of Fairway segment
          r: '66%',   // Outer radius - end of Fairway segment
          startAngle: startAngle,
          endAngle: endAngle
        },
        style: {
          fill: 'grey',
          stroke: 'none'
        },
        z: -1, // Behind everything including grid lines
        silent: true // Don't interfere with interactions
      });
    }
    
    return sectors;
  };

  // Generate graphic text elements for segment labels positioned further out
  const generateSegmentLabels = () => {
    const labels = [];
    // Position labels at the center of each segment ring, but further out
    // Tee is at ~16.5% (middle of 0-33%), Fairway at ~50% (middle of 33-67%), Green at ~83.5% (middle of 67-100%)
    const labelPositions = [
      { segment: 'Tee', radius: '20%' },      // Slightly outside Tee ring
      { segment: 'Fairway', radius: '75%' },    // Further out from Fairway ring
      { segment: 'Green', radius: '95%' }      // Near the edge for Green
    ];
    
    // Place labels at a fixed angle (e.g., top of chart at 0 degrees)
    const labelAngle = -90; // Top of chart
    const angleRad = (labelAngle * Math.PI) / 180;
    
    labelPositions.forEach(({ segment, radius }) => {
      // Convert percentage radius to pixel position
      // For polar charts, we'll use a simpler approach with positioning
      labels.push({
        type: 'text',
        left: 'center',
        top: 'center',
        style: {
          text: segment,
          fontSize: 12,
          fontWeight: 'bold',
          fill: '#333'
        },
        // Position using polar coordinates converted to x,y
        // This is approximate - we'll position at the top of the chart
        position: [0, 0], // Will be calculated based on radius
        z: 10
      });
    });
    
    // Actually, let's use a simpler approach - position labels at fixed positions
    // We'll place them at the top of the chart at different radial distances
    return [
      {
        type: 'text',
        left: '50%',
        top: '37.5%',
        style: {
          text: 'Tee',
          fontSize: 12,
          fontWeight: 'bold',
          fill: '#333',
          textAlign: 'center'
        },
        z: 10
      },
      {
        type: 'text',
        left: '50%',
        top: '24.5%',
        style: {
          text: 'Fairway',
          fontSize: 12,
          fontWeight: 'bold',
          fill: '#333',
          textAlign: 'center'
        },
        z: 10
      },
      {
        type: 'text',
        left: '50%',
        top: '11%',
        style: {
          text: 'Green',
          fontSize: 12,
          fontWeight: 'bold',
          fill: '#333',
          textAlign: 'center'
        },
        z: 10
      }
    ];
  };

  const option = {
    tooltip: {
      formatter: (params: any) => {
        // Get the data point from chartData using the dataIndex
        const dataIndex = params.dataIndex;
        const dataPoint = chartData[dataIndex];
        if (!dataPoint) return '';
        
        const holeNumber = dataPoint.holeIndex + 1;
        const groupCount = dataPoint.groupCount;
        const segmentName = dataPoint.segment 
          ? dataPoint.segment.charAt(0).toUpperCase() + dataPoint.segment.slice(1)
          : segments[dataPoint.segmentIndex];
        
        if (groupCount === 1) {
          return `1 group on ${segmentName} of Hole ${holeNumber}`;
        } else {
          return `${groupCount} groups on ${segmentName} of Hole ${holeNumber}`;
        }
      }
    },
    graphic: [...generateFairwayBackground(), ...generateSegmentLabels()],
    polar: {},
    angleAxis: {
      type: 'category',
      data: holes,
      boundaryGap: false,
      splitLine: {
        show: true,
        lineStyle: {
          color: '#666', // Color of the radial lines
          width: 1, // Thickness of the radial lines
          type: 'solid' // Line type: 'solid', 'dashed', 'dotted'
        }
      },
      axisLine: {
        show: false
      },
      axisLabel: {
        show: true,
        // Position labels in the center of each segment
        // Use a negative distance to move labels inward toward center
        distance: -30, // Adjust this value to position labels in the center
        formatter: (value: string) => {
          // Extract just the number from "Hole X"
          return value.replace('Hole ', '');
        },
        fontSize: 12,
        fontWeight: 'bold'
      }
    },
    radiusAxis: {
      type: 'category',
      data: segments,
      axisLine: {
        show: false
      },
      axisLabel: {
        show: false // Hide default labels, we'll use graphic elements instead
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#666',
          width: 2,
          type: 'solid'
        }
      }
    },
    series: [
      {
        name: 'Fairway Background',
        type: 'line',
        coordinateSystem: 'polar',
        // Create a line that goes around the complete circle at the fairway segment
        // Use more points for a smoother, complete circle
        data: [
          ...Array.from({ length: 73 }, (_, i) => [1, i / 4]), // 73 points to go from 0 to 18
          [1, 0] // Explicitly close the loop back to start
        ],
        smooth: true, // Smooth curve
        lineStyle: {
          width: 108, // Mega thick line to cover the fairway area
          color: 'lightgrey'
        },
        symbol: 'none', // No symbols
        z: -1, // Behind everything including grid lines
        silent: true, // Don't interfere with tooltips
        animation: false
      },
      {
        name: 'Groups',
        type: 'scatter',
        coordinateSystem: 'polar',
        symbolSize: (val: any) => {
          // Base size for 1 group, increase by 4 pixels per additional group
          const groupCount = Array.isArray(val) ? val[2] : (val.groupCount || 1);
          return 8 + (groupCount - 1) * 4;
        },
        data: chartData.map((item) => ({
          value: item.value,
          itemStyle: item.itemStyle
        })),
        z: 10, // Above the background
        animationDelay: function (idx: number) {
          return idx * 5;
        }
      }
    ]
  };

  return (
    <ReactECharts 
      key={`pace-of-play-chart-2`}
      option={option} 
      className="cancelSelectorName" 
      style={{ height: '800px', width: '100%' }} 
      opts={{ renderer: 'canvas' }}
      notMerge={false}
      lazyUpdate={true}
    />
  );
}

